import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';
import DownloadsPanel from './DownloadsPanel';

const PANEL_WIDTH = 380;
const VIEWPORT_MARGIN = 12;


function computePanelPosition(pillRect) {
    if (!pillRect) {
        return null;
    }
    const maxLeft = window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(pillRect.left, maxLeft));
    return { left, top: pillRect.bottom + 6 };
}

const useStyles = makeStyles(() => ({
    root: {
        position: 'relative',
        marginRight: 8,
        display: 'inline-flex',
        alignItems: 'center',
    },
    pill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 30,
        padding: '0 12px',
        borderRadius: 999,
        border: '2px solid #e2e2e2',
        background: '#fff',
        cursor: 'pointer',
        userSelect: 'none',
    },
    pillActive: {
        borderColor: '#c7e2f5',
        background: '#f3f9fe',
    },
    ring: {
        width: 15,
        height: 15,
        flexShrink: 0,
    },
    ringSpin: {
        animation: '$spin 1.1s linear infinite',
    },
    '@keyframes spin': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' },
    },
    ringTrack: {
        fill: 'none',
        stroke: '#e0e0e0',
        strokeWidth: 3,
    },
    ringFill: {
        fill: 'none',
        stroke: '#0d47a1',
        strokeWidth: 3,
        strokeLinecap: 'round',
        transition: 'stroke-dashoffset .3s ease',
    },
    icon: {
        fontSize: 16,
        color: '#337AB7',
    },
    label: {
        fontSize: 11.5,
        fontWeight: 600,
        color: '#333',
        whiteSpace: 'nowrap',
    },
    badge: {
        background: '#0d47a1',
        color: '#fff',
        borderRadius: 9,
        fontSize: 9.5,
        fontWeight: 700,
        minWidth: 14,
        height: 14,
        lineHeight: '14px',
        textAlign: 'center',
        padding: '0 4px',
    },
    '@media (prefers-reduced-motion: reduce)': {
        ringSpin: { animation: 'none' },
    },
    // Dark theme overrides, added to the root only when the app toggle is off.
    dark: {
        '& $pill': { borderColor: '#555', background: '#333' },
        '& $pillActive': { borderColor: '#35597a', background: '#26313b' },
        '& $ringTrack': { stroke: '#555' },
        '& $ringFill': { stroke: '#64b5f6' },
        '& $icon': { color: '#64b5f6' },
        '& $label': { color: '#eee' },
    },
}));

const RING_RADIUS = 9;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function ProgressRing({ percent, classes }) {
    if (percent == null) {
        return (
            <svg className={`${classes.ring} ${classes.ringSpin}`} viewBox="0 0 24 24">
                <circle className={classes.ringTrack} cx="12" cy="12" r={RING_RADIUS} />
                <circle
                    className={classes.ringFill}
                    cx="12" cy="12" r={RING_RADIUS}
                    strokeDasharray={`${RING_CIRCUMFERENCE * 0.25} ${RING_CIRCUMFERENCE}`}
                />
            </svg>
        );
    }
    const clamped = Math.max(0, Math.min(100, percent));
    const offset = RING_CIRCUMFERENCE * (1 - clamped / 100);
    return (
        <svg className={classes.ring} viewBox="0 0 24 24">
            <circle className={classes.ringTrack} cx="12" cy="12" r={RING_RADIUS} />
            <circle
                className={classes.ringFill}
                cx="12" cy="12" r={RING_RADIUS}
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={offset}
                transform="rotate(-90 12 12)"
            />
        </svg>
    );
}

export default function DownloadsIndicator() {
    const classes = useStyles();
    const toggle = useSelector((state) => state.setToggle.toggle);
    const jobs = useSelector((state) => state.setExportProgress.jobs);
    const [open, setOpen] = useState(false);
    const [rect, setRect] = useState(null);
    const rootRef = useRef(null);
    const panelRef = useRef(null);
    const closeTimeoutRef = useRef(null);

    const activeJobs = jobs.filter((j) => j.status === 'in-progress');
    const activeCount = activeJobs.length;
    const isActive = activeCount > 0;

    const updateRect = () => {
        if (rootRef.current) {
            setRect(rootRef.current.getBoundingClientRect());
        }
    };

    const cancelScheduledClose = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    };

    // Close on a short delay rather than immediately: the panel sits a few
    // pixels below the pill, so a direct mouseleave would fire while the
    // cursor is still crossing that gap on its way into the panel.
    const scheduleClose = () => {
        cancelScheduledClose();
        closeTimeoutRef.current = setTimeout(() => setOpen(false), 200);
    };

    useEffect(() => () => cancelScheduledClose(), []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            const insideRoot = rootRef.current && rootRef.current.contains(e.target);
            const insidePanel = panelRef.current && panelRef.current.contains(e.target);
            if (!insideRoot && !insidePanel) {
                cancelScheduledClose();
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // The panel is position:fixed (portaled out of any clipping ancestor), so its
    // coordinates have to be recomputed whenever the pill moves under it.
    useLayoutEffect(() => {
        if (!open) {
            return undefined;
        }
        updateRect();
        window.addEventListener('scroll', updateRect, true);
        window.addEventListener('resize', updateRect);
        return () => {
            window.removeEventListener('scroll', updateRect, true);
            window.removeEventListener('resize', updateRect);
        };
    }, [open]);

    let label = null;
    let ringPercent = null;
    if (activeCount === 1) {
        ringPercent = activeJobs[0].percent;
        label = ringPercent != null ? `Downloading ${ringPercent}%` : 'Downloading…';
    } else if (activeCount > 1) {
        label = 'Downloading…';
    }

    if (jobs.length === 0) {
        return null;
    }

    return (
        <div
            className={`${classes.root} ${toggle ? '' : classes.dark}`}
            ref={rootRef}
            onMouseEnter={() => { cancelScheduledClose(); setOpen(true); }}
            onMouseLeave={scheduleClose}
        >
            <div
                className={`${classes.pill} ${isActive ? classes.pillActive : ''}`}
                onClick={() => { cancelScheduledClose(); setOpen((o) => !o); }}
                role="button"
                tabIndex={0}
                aria-label={label || 'Downloads'}
                title={label || 'Downloads'}
            >
                {isActive
                    ? <ProgressRing percent={activeCount === 1 ? ringPercent : null} classes={classes} />
                    : <GetAppIcon className={classes.icon} />}
                {label && <span className={classes.label}>{label}</span>}
                {activeCount > 1 && <span className={classes.badge}>{activeCount}</span>}
            </div>
            {open && rect && ReactDOM.createPortal(
                <div
                    ref={panelRef}
                    style={{ position: 'fixed', ...computePanelPosition(rect), width: PANEL_WIDTH, zIndex: 1400 }}
                    onMouseEnter={() => { cancelScheduledClose(); setOpen(true); }}
                    onMouseLeave={scheduleClose}
                >
                    <DownloadsPanel jobs={jobs} onClose={() => setOpen(false)} />
                </div>,
                document.body
            )}
        </div>
    );
}
